import { Text } from '@/components/text';
import { LANGUAGES } from '@/constants/languages';
import { Colors } from '@/constants/theme';
import { useLanguageStore } from '@/stores/language-store';
import { useState } from 'react';
import { LayoutChangeEvent, Pressable, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, withSpring, withTiming } from 'react-native-reanimated';

const AnimatedText = Animated.createAnimatedComponent(Text);

export function LanguageSelector() {
  const { locale, setLocale } = useLanguageStore();
  const [containerWidth, setContainerWidth] = useState(0);
  const languageKeys = Object.keys(LANGUAGES) as (keyof typeof LANGUAGES)[];
  const activeIndex = languageKeys.indexOf(locale as keyof typeof LANGUAGES);

  const tabWidth = containerWidth / languageKeys.length;

  const onLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  const indicatorStyle = useAnimatedStyle(() => {
    if (containerWidth === 0) return {};
    return {
      transform: [
        {
          translateX: withSpring(activeIndex * tabWidth, {
            damping: 30,
            stiffness: 350,
          }),
        },
      ],
      width: tabWidth,
    };
  });

  return (
    <View style={styles.container} onLayout={onLayout}>
      {/* Background Indicator */}
      {containerWidth > 0 && <Animated.View style={[styles.indicator, indicatorStyle]} />}

      {/* Language Tabs */}
      {languageKeys.map((code, index) => {
        const isActive = activeIndex === index;
        return (
          <Pressable key={code} onPress={() => setLocale(code)} style={styles.tab}>
            <TabLabel code={code} isActive={isActive} />
          </Pressable>
        );
      })}
    </View>
  );
}

function TabLabel({ code, isActive }: { code: string; isActive: boolean }) {
  const animatedStyle = useAnimatedStyle(() => {
    const color = withTiming(isActive ? Colors.light.primary : Colors.light.icon, {
      duration: 200,
    });
    return { color };
  });

  return <AnimatedText style={[styles.text, animatedStyle]}>{code.toUpperCase()}</AnimatedText>;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    height: 36,
    width: 140,
  },
  indicator: {
    position: 'absolute',
    bottom: -1,
    left: 0,
    height: 2,
    backgroundColor: Colors.light.primary,
  },
  tab: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  text: {
    fontSize: 13,
    fontWeight: '600',
  },
});
